/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { OperationalZones } from '../../types/calculator';
import '../../styles/TooltipStyles.css';

interface ZoneOverlayProps {
  map: google.maps.Map;
  center: { lat: number; lng: number };
  zones: OperationalZones;
  onCenterChange?: (newCenter: { lat: number; lng: number }, panToNewCenter?: boolean) => void;
}

interface ZoneCircle {
  circle: google.maps.Circle;
  name: string;
  radius: number;
  color: string;
  index: number;
}

const ZoneOverlay: React.FC<ZoneOverlayProps> = ({ map, center, zones, onCenterChange }) => {
  const [zoneCircles, setZoneCircles] = useState<ZoneCircle[]>([]);
  const [currentCenter, setCurrentCenter] = useState(center);
  const [tooltipState, setTooltipState] = useState<{
    visible: boolean;
    text: string;
    position: { x: number; y: number };
  }>({
    visible: false,
    text: '',
    position: { x: 0, y: 0 }
  });

  // Create tooltip element when component mounts
  useEffect(() => {
    // Create tooltip element
    const tooltipEl = document.createElement('div');
    tooltipEl.className = 'circle-tooltip';
    tooltipEl.style.display = 'none';
    document.body.appendChild(tooltipEl);

    // Update tooltip when state changes
    const updateTooltip = () => {
      if (tooltipState.visible) {
        tooltipEl.style.display = 'block';
        tooltipEl.style.left = `${tooltipState.position.x}px`;
        tooltipEl.style.top = `${tooltipState.position.y}px`;
        tooltipEl.innerHTML = tooltipState.text;
      } else {
        tooltipEl.style.display = 'none';
      }
    };

    updateTooltip();

    // Cleanup on unmount
    return () => {
      document.body.removeChild(tooltipEl);
    };
  }, [tooltipState]);

  // Create and update circles based on zones and center
  useEffect(() => {
    // Clear previous circles
    zoneCircles.forEach(zoneCircle => {
      zoneCircle.circle.setMap(null);
    });

    // Get multiplier for unit system
    const multiplier = zones.unitSystem === 'imperial' ? 0.3048 : 1; // Convert feet to meters if imperial
    
    // Calculate radii in meters (Google Maps uses meters)
    const flightAreaRadius = zones.flightArea * multiplier;
    const softGeoFenceRadius = (zones.flightArea + zones.softGeoFence) * multiplier;
    const hardGeoFenceRadius = (zones.flightArea + zones.softGeoFence + zones.hardGeoFence) * multiplier;
    const restrictedAreaRadius = (zones.flightArea + zones.softGeoFence + zones.hardGeoFence + zones.totalRestrictedArea) * multiplier;
    
    // Format the radius values for display with exactly 1 decimal place
    const formatRadius = (radius: number): number => {
      return Number(radius.toFixed(1));
    };
    
    // Create new circles with formatted radius values
    const newCircles: ZoneCircle[] = [
      {
        circle: new google.maps.Circle({
          map,
          center: currentCenter,
          radius: restrictedAreaRadius,
          fillColor: 'rgba(231, 76, 60, 0.15)',
          fillOpacity: 0.5,
          strokeWeight: 1,
          strokeColor: '#E74C3C',
          zIndex: 1,
          clickable: true,
          draggable: false
        }),
        name: 'Restricted Area',
        radius: formatRadius(zones.totalRestrictedArea),
        color: 'rgba(231, 76, 60, 0.15)',
        index: 1
      },
      {
        circle: new google.maps.Circle({
          map,
          center: currentCenter,
          radius: hardGeoFenceRadius,
          fillColor: 'rgba(230, 126, 34, 0.25)',
          fillOpacity: 0.5,
          strokeWeight: 1,
          strokeColor: '#E67E22',
          zIndex: 2,
          clickable: true,
          draggable: false
        }),
        name: 'Hard Geo-fence',
        radius: formatRadius(zones.hardGeoFence),
        color: 'rgba(230, 126, 34, 0.25)',
        index: 2
      },
      {
        circle: new google.maps.Circle({
          map,
          center: currentCenter,
          radius: softGeoFenceRadius,
          fillColor: 'rgba(241, 196, 15, 0.3)',
          fillOpacity: 0.5,
          strokeWeight: 1,
          strokeColor: '#F1C40F',
          zIndex: 3,
          clickable: true,
          draggable: false
        }),
        name: 'Soft Geo-fence',
        radius: formatRadius(zones.softGeoFence),
        color: 'rgba(241, 196, 15, 0.3)',
        index: 3
      },
      {
        circle: new google.maps.Circle({
          map,
          center: currentCenter,
          radius: flightAreaRadius,
          fillColor: 'rgba(52, 152, 219, 0.4)',
          fillOpacity: 0.5,
          strokeWeight: 1,
          strokeColor: '#3498DB',
          zIndex: 4,
          clickable: true,
          draggable: false
        }),
        name: 'Flight Area',
        radius: formatRadius(zones.flightArea),
        color: 'rgba(52, 152, 219, 0.4)',
        index: 4
      }
    ];

    // Add event listeners for each circle
    newCircles.forEach((zoneCircle) => {
      // Show tooltip on mousemove
      google.maps.event.addListener(zoneCircle.circle, 'mousemove', (e: google.maps.MapMouseEvent) => {
        if (e.domEvent && e.latLng) {
          const unitLabel = zones.unitSystem === 'imperial' ? 'ft' : 'm';
          
          // Get the actual current radius in map units and convert to display units
          const radiusInMapUnits = zoneCircle.circle.getRadius();
          const radiusInDisplayUnits = zones.unitSystem === 'imperial' 
            ? radiusInMapUnits / 0.3048 // Convert meters to feet if imperial
            : radiusInMapUnits; // Keep as meters if metric
          
          // Format to exactly 1 decimal place
          const formattedRadius = radiusInDisplayUnits.toFixed(1);
          
          const content = `
            <strong>${zoneCircle.name}</strong>
            Radius: ${formattedRadius} ${unitLabel}
          `;

          // Get mouse position from event
          const mouseEvent = e.domEvent as MouseEvent;
          
          setTooltipState({
            visible: true,
            text: content,
            position: {
              x: mouseEvent.clientX,
              y: mouseEvent.clientY - 10 // Position slightly above cursor
            }
          });
        }
      });
      
      // Hide tooltip on mouseout
      google.maps.event.addListener(zoneCircle.circle, 'mouseout', () => {
        setTooltipState(prevState => ({
          ...prevState,
          visible: false
        }));
      });
    });

    setZoneCircles(newCircles);

    // Add drone marker at center (draggable)
    const droneMarker = new google.maps.Marker({
      position: currentCenter,
      map,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 7,
        fillColor: '#FFFFFF',
        fillOpacity: 0.9,
        strokeWeight: 1,
        strokeColor: '#000000'
      },
      zIndex: 5,
      draggable: true,
      title: 'Drone Location (drag to move all zones)'
    });
    
    // Add drag event listener to drone marker
    google.maps.event.addListener(droneMarker, 'drag', () => {
      // Update circles in real-time during dragging (not just at end)
      const newCenter = droneMarker.getPosition();
      if (newCenter) {
        // Update all circles to the new center
        newCircles.forEach((circle) => {
          circle.circle.setCenter(newCenter);
        });
      }
    });
    
    // Capture the map's viewport before starting drag
    let mapViewport: { center: google.maps.LatLng | null; zoom: number | null } = { 
      center: null,
      zoom: null
    };
    
    // When drag starts, store current map position/zoom
    google.maps.event.addListener(droneMarker, 'dragstart', () => {
      mapViewport.center = map.getCenter() || null;
      mapViewport.zoom = map.getZoom() || null;
    });
    
    // Update state at the end of drag
    google.maps.event.addListener(droneMarker, 'dragend', () => {
      const newCenter = droneMarker.getPosition();
      if (newCenter) {
        const newPos = { lat: newCenter.lat(), lng: newCenter.lng() };
        
        // Update our internal state without moving the map
        setCurrentCenter(newPos);
        
        // Notify parent component about center change to update coordinate display
        if (onCenterChange) {
          // Important: Don't pan/recenter the map
          onCenterChange(newPos, false);
          
          // After state updates, restore the map's position
          // Use setTimeout to ensure this runs after React state updates
          setTimeout(() => {
            if (mapViewport.center) {
              map.setCenter(mapViewport.center);
            }
            if (mapViewport.zoom !== null) {
              map.setZoom(mapViewport.zoom);
            }
          }, 0);
        }
      }
    });

    // Clean up on unmount
    return () => {
      newCircles.forEach((zoneCircle) => {
        zoneCircle.circle.setMap(null);
        google.maps.event.clearInstanceListeners(zoneCircle.circle);
      });
      droneMarker.setMap(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, currentCenter, zones, onCenterChange]); // zoneCircles is intentionally excluded to avoid infinite loop

  // Update internal center state when prop changes (but not during our own updates)
  useEffect(() => {
    // Only update from props if it's different from our current state
    if (center.lat !== currentCenter.lat || center.lng !== currentCenter.lng) {
      setCurrentCenter(center);
    }
  }, [center, currentCenter]);

  // Nothing to render directly - the circles are added to the map
  return null;
};

export default ZoneOverlay;
